'use strict';

import i18next from 'i18next';
import moment from 'moment-timezone';
import { getColour } from '../../shared/components/chart/chart-helpers.js';
import { Utils } from '../../utils/utils.js';
import {
  calculateHoursPerDay,
  renderBalanceErniGraph,
} from '../balance/balance.component.js';

const ERNI_SNP_COLOURS = ['#046BBC', '#DC878A', '#9C9C9C'];
const ERNI_SNP_LINE_COLOUR = '#9C9C9C';

const MONTH_NAMES = {
  es: [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sept',
    'Oct',
    'Nov',
    'Dic',
  ],
  en: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
  ],
};

const generateMonthlyDates = (startDate, endDate, valuesList) => {
  let dates = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));

    const nextDate = new Date(currentDate);
    nextDate.setMonth(nextDate.getMonth() + 1);
    currentDate = nextDate;
  }

  let existingDatesSet = new Set(valuesList.map((entry) => entry.x.getTime()));

  dates.forEach((dateValue) => {
    if (!existingDatesSet.has(dateValue.getTime())) {
      valuesList.push({ x: dateValue, y: null });
    }
  });

  return valuesList;
};

const fillPrintVals = (rangeNumber, date) => {
  let baseDate = date;
  if (baseDate === undefined) {
    baseDate = moment().format('DD-MM-YYYY');
  }

  let arr = [];
  let dateSplit = baseDate.split('-');
  let utcTime = moment(baseDate, 'DD-MM-YYYY').tz('Europe/Madrid').format();

  for (let i = 0; i < rangeNumber; i++) {
    arr.push({
      datetime:
        `${dateSplit[2]}-${dateSplit[1]}-${dateSplit[0]}T${
          i < 10 ? '0' + i : i
        }:00:00.000+` + utcTime.split('+')[1],
      datetime_utc: '2022-02-04T23:00:00Z',
      geo_id: null,
      geo_name: null,
      tz_time: '2022-02-04T23:00:00.000Z',
      value: 0,
    });
  }

  return arr;
};

const fillSimpleVals = () => {
  let arr = [];
  for (let i = 0; i < 14; i++) {
    arr.push({
      x: i,
      order: i,
      y: null,
      value: null,
      color: '#333333',
      name: '',
    });
  }
  return arr;
};

const getErniMonthLabels = (date, totalPoints) => {
  const language = i18next.language === 'es' ? 'es' : 'en';
  const monthNames = MONTH_NAMES[language];
  const now = moment().tz('Europe/Madrid');
  const isPrint =
    new URLSearchParams(window.location.search).get('print') === 'true';
  let endDate = moment(date, 'DD-MM-YYYY').tz('Europe/Madrid').startOf('month');

  if (endDate.isSame(now, 'month')) {
    endDate = endDate.subtract(1, 'month');
  }

  return Array.from({ length: totalPoints }, (_, index) => {
    const monthDate = moment(endDate).subtract(
      totalPoints - index - 1,
      'months'
    );
    return {
      key: index,
      label: isPrint
        ? monthNames[monthDate.month()]
        : `${monthNames[monthDate.month()]} ${monthDate.year()}`,
    };
  });
};

const renderErniSnpBarsLineGraph = (indicators, opts) => {
  const container = document.querySelector(opts.elem);
  if (!container) {
    return false;
  }

  d3.select(container).select('svg').remove();

  const barIndicators = indicators.filter((indicator) => !indicator.demanda);
  const lineIndicator = indicators.find((indicator) => indicator.demanda);
  const totalPoints = Math.max(
    0,
    ...indicators.map((indicator) => indicator.values?.length || 0)
  );

  if (!totalPoints) {
    return false;
  }

  const labels = getErniMonthLabels(opts.date, totalPoints);
  const chartData = labels.map((labelItem, index) => ({
    ...labelItem,
    rrtt: barIndicators[0]?.values?.[index]?.value ?? null,
    balance: barIndicators[1]?.values?.[index]?.value ?? null,
    total: lineIndicator?.values?.[index]?.value ?? null,
  }));

  const chartValues = chartData.flatMap((item) => [
    item.rrtt,
    item.balance,
    item.total,
  ]);
  const hasAnyValue = chartValues.some(
    (value) => value != null && !Number.isNaN(value)
  );
  if (!hasAnyValue) {
    return false;
  }
  const maxValue = chartValues.reduce((acc, value) => {
    if (value == null || Number.isNaN(value)) {
      return acc;
    }
    return Math.max(acc, value);
  }, 0);

  const parentWidth =
    container.parentElement?.getBoundingClientRect().width ||
    container.getBoundingClientRect().width ||
    1200;
  const margins = {
    top: 34,
    right: 24,
    bottom: parentWidth < 900 ? 108 : 84,
    left: 50,
  };
  const width = Math.max(parentWidth - margins.left - margins.right, 360);
  const innerHeight = width < 700 ? 290 : 335;
  const totalHeight = innerHeight + margins.top + margins.bottom;
  const svgWidth = width + margins.left + margins.right;

  const svg = d3
    .select(container)
    .append('svg')
    .attr('width', svgWidth)
    .attr('height', totalHeight)
    .attr('viewBox', `0 0 ${svgWidth} ${totalHeight}`)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .style('width', '100%')
    .style('height', 'auto')
    .style('display', 'block')
    .style('margin', '0 auto');

  const chart = svg
    .append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  const yMax = Math.max(10, Math.ceil(maxValue));
  const x0 = d3.scale
    .ordinal()
    .domain(chartData.map((item) => item.key))
    .rangeRoundBands([0, width], 0.35);
  const y = d3.scale.linear().domain([0, yMax]).range([innerHeight, 0]);
  const barWidth = Math.max(
    18,
    Math.min(34, Math.round(x0.rangeBand() * 0.74))
  );

  const yAxis = d3.svg
    .axis()
    .scale(y)
    .orient('left')
    .ticks(10)
    .tickFormat((value) => value.toFixed(1))
    .tickSize(0);

  const xAxis = d3.svg
    .axis()
    .scale(x0)
    .orient('bottom')
    .tickFormat((key) => chartData[key]?.label || '');

  chart
    .selectAll('.erni-grid-line')
    .data(y.ticks(10))
    .enter()
    .append('line')
    .attr('class', 'erni-grid-line')
    .attr('x1', 0)
    .attr('x2', width)
    .attr('y1', (value) => y(value))
    .attr('y2', (value) => y(value))
    .attr('stroke', '#C9D2DB')
    .attr('stroke-dasharray', '8,6')
    .attr('shape-rendering', 'crispEdges');

  chart
    .append('g')
    .attr('class', 'erni-y-axis')
    .call(yAxis)
    .call((axis) => {
      axis.selectAll('path').style('display', 'none');
      axis.selectAll('line').style('display', 'none');
      axis
        .selectAll('text')
        .style('fill', '#5B6670')
        .style('font-size', '15px')
        .style('font-weight', '600');
    });

  const monthGroups = chart
    .selectAll('.erni-month-group')
    .data(chartData)
    .enter()
    .append('g')
    .attr('class', 'erni-month-group')
    .attr('transform', (item) => `translate(${x0(item.key)},0)`);

  monthGroups
    .append('rect')
    .attr('class', 'erni-bar erni-bar-rrtt')
    .attr('x', (x0.rangeBand() - barWidth) / 2)
    .attr('y', (item) => (item.rrtt == null ? innerHeight : y(item.rrtt)))
    .attr('width', barWidth)
    .attr('height', (item) =>
      item.rrtt == null ? 0 : Math.max(innerHeight - y(item.rrtt), 0)
    )
    .attr('fill', ERNI_SNP_COLOURS[0]);

  monthGroups
    .append('rect')
    .attr('class', 'erni-bar erni-bar-balance')
    .attr('x', (x0.rangeBand() - barWidth) / 2)
    .attr('y', (item) => {
      if (item.balance == null || item.balance === 0) {
        return innerHeight;
      }
      const stackedValue = (item.rrtt || 0) + item.balance;
      return y(stackedValue);
    })
    .attr('width', barWidth)
    .attr('height', (item) => {
      if (item.balance == null || item.balance === 0) {
        return 0;
      }
      return Math.max(
        y(item.rrtt || 0) - y((item.rrtt || 0) + item.balance),
        0
      );
    })
    .attr('fill', ERNI_SNP_COLOURS[1]);

  const line = d3.svg
    .line()
    .defined((item) => item.total != null)
    .x((item) => x0(item.key) + x0.rangeBand() / 2)
    .y((item) => y(item.total))
    .interpolate('linear');

  chart
    .append('path')
    .datum(chartData)
    .attr('class', 'erni-total-line')
    .attr('d', line)
    .attr('fill', 'none')
    .attr('stroke', ERNI_SNP_LINE_COLOUR)
    .attr('stroke-width', 1.2);

  chart
    .append('g')
    .attr('class', 'erni-x-axis')
    .attr('transform', `translate(0,${innerHeight})`)
    .call(xAxis)
    .call((axis) => {
      axis.selectAll('path').style('display', 'none');
      axis.selectAll('line').style('display', 'none');
      axis
        .selectAll('text')
        .style('fill', '#6B5C4B')
        .style('font-size', '13px')
        .style('font-weight', '700')
        .style('text-anchor', 'middle')
        .attr('dy', '1.1em');
    });

  chart
    .append('text')
    .attr('x', -18)
    .attr('y', -12)
    .style('fill', '#000000')
    .style('font-size', '15px')
    .style('font-weight', '600')
    .text('%');

  chart
    .append('text')
    .attr('x', -28)
    .attr('y', innerHeight + 22)
    .style('fill', '#000000')
    .style('font-size', '14px')
    .style('font-weight', '600')
    .text(i18next.t('months'));

  const legendItems = [
    {
      label:
        i18next.language === 'es'
          ? 'Porcentaje ERNI RRTT SNP'
          : 'Percentage ERNI RRTT SNP',
      color: ERNI_SNP_COLOURS[0],
      type: 'bar',
    },
    {
      label:
        i18next.language === 'es'
          ? 'Porcentaje ERNI Balance SNP'
          : 'Percentage ERNI Balance SNP',
      color: ERNI_SNP_COLOURS[1],
      type: 'bar',
    },
    {
      label:
        i18next.language === 'es'
          ? 'Porcentaje ERNI Total SNP'
          : 'Percentage ERNI Total SNP',
      color: ERNI_SNP_LINE_COLOUR,
      type: 'line',
    },
  ];
  const legend = chart
    .selectAll('.erni-legend-item')
    .data(legendItems)
    .enter()
    .append('g')
    .attr('class', 'erni-legend-item');

  legend.each(function (item) {
    const legendItem = d3.select(this);

    if (item.type === 'line') {
      legendItem
        .append('line')
        .attr('x1', 0)
        .attr('x2', 16)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', item.color)
        .attr('stroke-width', 3);
    } else {
      legendItem
        .append('rect')
        .attr('x', 0)
        .attr('y', -2)
        .attr('width', 12)
        .attr('height', 4)
        .attr('fill', item.color);
    }

    legendItem
      .append('text')
      .attr('x', 22)
      .attr('y', 4)
      .style('fill', '#000000')
      .style('font-size', '14px')
      .style('font-weight', '600')
      .text(item.label);
  });

  const legendNodes = legend[0] || [];
  const legendRows = width < 900 ? [[0, 1], [2]] : [[0, 1, 2]];
  const legendGap = width < 900 ? 28 : 40;
  const legendBaseY = innerHeight + 56;

  legendRows.forEach((row, rowIndex) => {
    const rowWidths = row.map(
      (index) => legendNodes[index]?.getBBox().width || 0
    );
    const totalRowWidth =
      rowWidths.reduce((sum, itemWidth) => sum + itemWidth, 0) +
      legendGap * Math.max(row.length - 1, 0);
    let currentX = Math.max((width - totalRowWidth) / 2, 0);
    const currentY = legendBaseY + rowIndex * 24;

    row.forEach((legendIndex, itemIndex) => {
      d3.select(legendNodes[legendIndex]).attr(
        'transform',
        `translate(${currentX},${currentY})`
      );
      currentX += rowWidths[itemIndex] + legendGap;
    });
  });

  return true;
};

const renderErniWidget = async (
  data,
  date,
  graphic,
  noDataGraphic,
  graphicalmuadilla,
  snp,
  datason
) => {
  let isMobile = Utils.isMobileDevice();
  let routeParts = window.location.pathname.split('/');
  let language = routeParts[1];
  let erniTotalName = 'Porcentaje ERNI Total SNP';

  if (language == 'en') {
    erniTotalName = 'ERNI Percentage of Total SNP';
  }

  const indicators = data.indicators;

  indicators.forEach((indicator) => {
    switch (indicator.id) {
      case 10488:
        if (language == 'en') {
          indicator.name = 'Percentage ERNI RRTT SNP';
        } else {
          indicator.name = 'Porcentaje ERNI RRTT SNP';
        }
        break;
      case 10489:
        if (language == 'en') {
          indicator.name = 'ERNI Percentage SNP Balance';
        } else {
          indicator.name = 'Porcentaje ERNI Balance SNP';
        }
        break;
      case 10490:
        if (language == 'en') {
          indicator.name = 'ERNI Percentage Total SNP';
        } else {
          indicator.name = 'Porcentaje ERNI Total SNP';
        }
        break;
      default:
        break;
    }
  });

  let opts = {
    type: 'multi',
    special: true,
    fit: true,
    multibar: true,
    margin: { top: 20, right: 0, bottom: 40, left: 50 },
    lineInterpolation: 'linear',
    elem: graphicalmuadilla,
    height: isMobile ? 380 : 500,
    date: date,
    yticks: 10,
    left: {
      label: '%',
    },
    bottom: {
      label: 'months',
      prefix: '0',
    },
    magnitud: '%',
  };

  date = date.replace(/^\d{2}-/, '01-');
  let valh = calculateHoursPerDay(date);
  if (valh !== 25) {
    valh = 24;
  }

  let preData = {
    ...data,
    indicators: data.indicators.map((el) => ({
      ...el,
      values: fillSimpleVals(valh),
    })),
  };

  let dataOrdered = Utils.orderObjectsArrayByKey(
    preData.indicators,
    [10488, 10489, 10490],
    'id'
  );

  const currentDate = new Date();
  const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
  const currentYear = currentDate.getFullYear();

  let dateParts = date.split('-');
  let monthFromDate = dateParts[1];
  let yearFromDate = dateParts[2];

  const dateObject = moment(date, 'DD-MM-YYYY').toDate();
  let iniDateObject = moment(dateObject).subtract(13, 'months').toDate();

  if (currentMonth == monthFromDate && currentYear == yearFromDate) {
    iniDateObject = moment(dateObject).subtract(14, 'months').toDate();
  }

  dataOrdered.map((el, index) => {
    let finded =
      data.indicators.filter((or) => or.id === el.id)[0]?.values || [];
    let currentIndexValues = 0;

    if (finded.length > 0) {
      let firstFindedDate = moment(finded[0].x).toDate();
      if (iniDateObject < firstFindedDate) {
        let tempIniDateObject = iniDateObject;
        while (tempIniDateObject < firstFindedDate) {
          tempIniDateObject = moment(tempIniDateObject)
            .add(1, 'months')
            .toDate();
          currentIndexValues++;
        }
      }
    }

    let tempIniDateObject = moment(dateObject)
      .subtract(14, 'months')
      .startOf('month')
      .toDate();

    if (finded.length > 0) {
      let completedValues = generateMonthlyDates(
        moment(finded[0].x).toDate(),
        moment(finded[finded.length - 1].x).toDate(),
        finded
      );

      completedValues.sort((a, b) => a.x - b.x);
      finded = completedValues;
    }

    finded.forEach((val) => {
      let findedDate = moment(val.x).startOf('month').toDate();

      if (findedDate < tempIniDateObject) {
        return;
      }

      el.values[currentIndexValues] = {
        ...el.values[currentIndexValues],
        value: val.y,
        y: val.y,
        color: ERNI_SNP_COLOURS[index] || getColour(index),
        name: el.name != null ? el.name : el.alias,
      };

      currentIndexValues++;
    });
  });

  if (preData.indicators.length !== 0) {
    preData.indicators.push({
      id: 10490,
      name: erniTotalName,
      shortName: '% Energía RRTT Total',
      color: '#9C9C9C',
      legendtype: 'line',
      demanda: true,
      values: fillSimpleVals(valh),
      print_values: fillPrintVals(valh, date),
    });
  }

  preData.indicators.map((elem, index) => {
    if (index === 2) {
      preData.indicators[3].values = preData.indicators[2]?.values.map(
        (value, valueIndex) => {
          if (
            preData.indicators[3]?.values[valueIndex] != null &&
            preData.indicators[3]?.values[valueIndex].y != null
          ) {
            return {
              ...value,
              y: value.y + preData.indicators[3]?.values[valueIndex].y,
              value: value.y + preData.indicators[3]?.values[valueIndex].y,
            };
          }

          return {
            ...value,
            y: value.y + 0,
            value: value.y + 0,
          };
        }
      );

      preData.indicators[3].print_values =
        preData.indicators[2]?.print_values.map((value, valueIndex) => {
          if (preData.indicators[3]?.values[valueIndex] != null) {
            return {
              ...value,
              y: preData.indicators[3]?.values[valueIndex].y,
              value: preData.indicators[3]?.values[valueIndex].value,
            };
          }

          return {
            ...value,
            y: 0,
            value: 0,
          };
        });

      preData.indicators.splice(2, 1);
    }
  });

  const dataLength =
    preData.indicators.length !== 0
      ? preData.indicators[0].print_values.length
      : preData.indicators.length;

  const noDataRect = document.getElementById(noDataGraphic);
  const widget = document.getElementById(graphic);

  if (dataLength !== 0) {
    if (widget != null) {
      widget.classList.remove('no-data-graphic');
    }
    if (noDataRect != null) {
      noDataRect.classList.add('hidden');
    }

    if (snp) {
      const rendered = renderErniSnpBarsLineGraph(preData.indicators, opts);
      if (!rendered) {
        if (widget != null) {
          widget.classList.add('no-data-graphic');
        }
        if (noDataRect != null) {
          noDataRect.classList.remove('hidden');
        }
      }
    } else {
      renderBalanceErniGraph(
        preData.indicators,
        i18next.t('month'),
        opts,
        snp,
        datason || []
      );
    }
  } else {
    if (widget != null) {
      widget.classList.add('no-data-graphic');
    }
    if (noDataRect != null) {
      noDataRect.classList.remove('hidden');
    }
    if (document.querySelector(graphicalmuadilla + ' > svg') != null) {
      document
        .querySelector(graphicalmuadilla + ' > svg')
        .classList.add('hidden');
    }
  }
};

let rendernoPeninsulares = (() => {
  return {
    renderErniWidget: (
      data,
      date,
      graphic,
      noDataGraphic,
      graphicalmuadilla,
      snp,
      datason
    ) =>
      renderErniWidget(
        data,
        date,
        graphic,
        noDataGraphic,
        graphicalmuadilla,
        snp,
        datason
      ),
  };
})();

export { rendernoPeninsulares };
